import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { useSidebar } from "@/contexts/SidebarContext";
import { SECTION } from "@/constants/examConstants";
import { selectIsAuthenticated } from "@/store/slices/authSlice";
import { normalizeExam } from "@/utils/normalizeExam";
import {
  useGetPublicMockTestQuery,
  useSubmitPublicMockTestMutation,
} from "@/store/api/user/publicMockTestApi";

// Shared exam building blocks — same hook, same components as ExamSession.
import useExamFlow from "@/hooks/useExamFlow";
import WelcomeScreen from "@/pages/user/ExamSession/WelcomeScreen";
import PartInstruction from "@/pages/user/ExamSession/PartInstruction";
import NavigationBar from "@/pages/user/ExamSession/NavigationBar";
import QuestionsDialog from "@/pages/user/ExamSession/QuestionsDialog";
import ExamAudioElement from "@/pages/user/ExamSession/ExamAudioElement";
import ExamQuestionView from "@/pages/user/ExamSession/ExamQuestionView";
import ConfirmSubmitDialog from "@/pages/user/ExamSession/ConfirmSubmitDialog";

import { partLabel } from "./partMeta";
import Result from "./Result";
import {
  clearPublicMockDraft,
  loadPublicMockDraft,
  savePublicMockDraft,
  sweepStalePublicMockDrafts,
} from "./localStore";

const parsePartsParam = (raw) => {
  if (!raw) return [];
  return raw
    .split(",")
    .map((n) => parseInt(n.trim(), 10))
    .filter((n) => !Number.isNaN(n))
    .sort((a, b) => a - b);
};

const isMeaningfulAnswer = (value) => {
  if (value == null) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (Array.isArray(value)) return value.some((item) => isMeaningfulAnswer(item));
  if (typeof value === "object") {
    return Object.values(value).some((item) => isMeaningfulAnswer(item));
  }
  return true;
};

const findResumePositionByAnswers = (examParts = [], restoredAnswers = {}) => {
  if (!Array.isArray(examParts) || examParts.length === 0) return null;

  let lastAnsweredGroup = null;

  for (let partIndex = 0; partIndex < examParts.length; partIndex++) {
    const part = examParts[partIndex];
    const groups = part?.questionGroup || [];

    for (let groupIndex = 0; groupIndex < groups.length; groupIndex++) {
      const questions = groups[groupIndex]?.questions || [];
      const hasAnsweredInGroup = questions.some((q) =>
        isMeaningfulAnswer(restoredAnswers[q?._id])
      );

      if (hasAnsweredInGroup) {
        lastAnsweredGroup = {
          partIndex,
          groupIndex,
          inReading: part?.section === SECTION.READING,
        };
      }

      const firstUnanswered = questions.find(
        (q) => !isMeaningfulAnswer(restoredAnswers[q?._id])
      );

      if (firstUnanswered) {
        return {
          partIndex,
          groupIndex,
          inReading: part?.section === SECTION.READING,
        };
      }
    }
  }

  return (
    lastAnsweredGroup || {
      partIndex: 0,
      groupIndex: 0,
      inReading: examParts[0]?.section === SECTION.READING,
    }
  );
};

const Runner = () => {
  const { examId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { collapseSidebar } = useSidebar();
  const isAuthenticated = useSelector(selectIsAuthenticated);

  const mode = searchParams.get("mode") === "part" ? "part" : "full";
  const parts = useMemo(
    () => parsePartsParam(searchParams.get("parts")),
    [searchParams]
  );

  const {
    data: rawExam,
    isLoading,
    isError,
    error,
  } = useGetPublicMockTestQuery({
    examId,
    parts: mode === "part" ? parts : undefined,
  });

  const [submitPublicMockTest, { isLoading: isSubmitting }] =
    useSubmitPublicMockTestMutation();

  // Public exam payload uses `questionGroups` (plural) — normalize once so
  // the shared exam components don't need to know about either shape.
  const exam = useMemo(() => normalizeExam(rawExam), [rawExam]);

  // ── Test-specific state (answers, marks, submitted result) ──────────
  const [answers, setAnswers] = useState({});
  const [markedQuestions, setMarkedQuestions] = useState({});
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [openQuestionList, setOpenQuestionList] = useState(false);
  const [result, setResult] = useState(null);

  const startedAtRef = useRef(null);
  const hasHydratedRef = useRef(false);
  const hasAppliedDraftResumeRef = useRef(false);
  const loadedDraftRef = useRef(null);
  const submittedRef = useRef(false);
  const answersRef = useRef(answers);
  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  // ── Submit (single end-of-test grade call) ──────────────────────────
  const doSubmit = useCallback(async () => {
    if (submittedRef.current) return;
    submittedRef.current = true;

    flowRef.current?.pauseAudio();

    const startedAt = startedAtRef.current || new Date().toISOString();
    const timeSpent = Math.max(
      0,
      Math.round((Date.now() - new Date(startedAt).getTime()) / 1000)
    );

    const answerList = Object.entries(answersRef.current)
      .filter(([, value]) => value != null && value !== "")
      .map(([questionId, selectedOption]) => ({ questionId, selectedOption }));

    const body = {
      mode,
      ...(mode === "part" ? { parts } : {}),
      startedAt,
      timeSpent,
      answers: answerList,
    };

    try {
      const data = await submitPublicMockTest({ examId, body }).unwrap();
      setResult(data);
      clearPublicMockDraft(examId, mode, parts);
      window.scrollTo(0, 0);
    } catch (err) {
      submittedRef.current = false;
      toast.error(
        err?.data?.message || "Failed to submit the test. Please try again."
      );
    }
    // flowRef is read inside but is a ref; mode/parts/examId are stable per
    // mount in practice (URL params).
  }, [examId, mode, parts, submitPublicMockTest]);

  // ── Centralised exam flow (audio + navigation + reading timer) ──────
  const flow = useExamFlow({
    exam,
    mode,
    onTimeUp: doSubmit,
  });
  const flowRef = useRef(flow);
  useEffect(() => {
    flowRef.current = flow;
  });

  const {
    audioRef,
    showWelcomeScreen,
    setShowWelcomeScreen,
    showInstructions,
    setShowInstructions,
    setHasUserInteracted,
    currentPart,
    currentGroup,
    questionGroup,
    currentSection,
    currentPartIndex,
    currentGroupIndex,
    isLastQuestion,
    setAudioVolume,
    isPlaying,
    isFinishListening,
    timeLeft,
    isWaiting,
    handleNext,
    handlePrev,
    handleAudioEnded,
    handleAudioPlay,
    navigateToQuestion,
    allQuestions,
    resetFlow,
    resumeFrom,
  } = flow;

  // ── Local draft persistence ─────────────────────────────────────────
  useEffect(() => {
    sweepStalePublicMockDrafts();
  }, []);

  useEffect(() => {
    if (hasHydratedRef.current || !examId) return;
    const draft = loadPublicMockDraft(examId, mode, parts);
    loadedDraftRef.current = draft;
    if (draft) {
      if (draft.answers && typeof draft.answers === "object") {
        setAnswers(draft.answers);
      }
      if (draft.startedAt) startedAtRef.current = draft.startedAt;
    }
    if (!startedAtRef.current) {
      startedAtRef.current = new Date().toISOString();
    }
    hasHydratedRef.current = true;
  }, [examId, mode, parts]);

  useEffect(() => {
    // Once the attempt is submitted (or a result is already on screen) the
    // draft must stay cleared — without this guard, the Reading countdown's
    // setInterval keeps ticking after submit (only audio is paused), so
    // `timeLeft` keeps changing and this effect re-fires, silently
    // resurrecting the just-cleared draft with the answers/time snapshot
    // from submission and letting the student resume a graded attempt.
    if (!hasHydratedRef.current || !examId || submittedRef.current || result) {
      return;
    }
    const shouldPersistReadingRemaining =
      mode === "full" &&
      currentSection === SECTION.READING &&
      !showInstructions;

    savePublicMockDraft(examId, mode, parts, {
      answers,
      startedAt: startedAtRef.current,
      readingRemainingSeconds: shouldPersistReadingRemaining
        ? Math.max(0, Math.floor(timeLeft || 0))
        : null,
    });
  }, [
    answers,
    examId,
    mode,
    parts,
    currentSection,
    showInstructions,
    timeLeft,
    result,
  ]);

  useEffect(() => {
    if (!hasHydratedRef.current || hasAppliedDraftResumeRef.current) return;
    if (!exam?.parts?.length) return;

    hasAppliedDraftResumeRef.current = true;
    const draft = loadedDraftRef.current;
    if (!draft?.answers || typeof draft.answers !== "object") return;

    const hasAnyAnswer = Object.values(draft.answers).some((value) =>
      isMeaningfulAnswer(value)
    );
    if (!hasAnyAnswer) return;

    const resume = findResumePositionByAnswers(exam.parts, draft.answers);
    if (!resume) return;

    // `readingRemainingSeconds` is `null` whenever the draft was saved before
    // the student ever reached Reading (see localStore.js) — that must mean
    // "no seed, use the full default timer", NOT "0 seconds left". Number(null)
    // is 0 (finite!), so we guard with a plain typeof check instead of relying
    // on Number.isFinite alone, or a stale Listening-only draft silently zeroes
    // out the Reading countdown and auto-submits the instant Reading starts.
    const remainingSeconds =
      typeof draft.readingRemainingSeconds === "number" &&
      Number.isFinite(draft.readingRemainingSeconds)
        ? Math.max(0, Math.floor(draft.readingRemainingSeconds))
        : undefined;

    collapseSidebar?.();
    resumeFrom({
      partIndex: resume.partIndex,
      groupIndex: resume.groupIndex,
      remainingSeconds,
      startReadingTimer: resume.inReading,
    });
  }, [exam?.parts, collapseSidebar, resumeFrom]);

  // ── Answer + mark handlers ──────────────────────────────────────────
  const handleAnswerSelect = (questionId, selectedOption) => {
    setAnswers((prev) => ({ ...prev, [questionId]: selectedOption }));
  };

  const handleToggleMarkQuestion = useCallback((question) => {
    if (!question?._id) return;
    setMarkedQuestions((prev) => {
      const next = { ...prev };
      if (next[question._id]) delete next[question._id];
      else next[question._id] = true;
      return next;
    });
  }, []);

  const handleNavigateToQuestion = useCallback(
    (questionNumber) => {
      const ok = navigateToQuestion(questionNumber, { scope: "all" });
      if (ok) setOpenQuestionList(false);
    },
    [navigateToQuestion]
  );

  // ── Build the AnswerReviewDialog-compatible shape from /submit result ─
  const examParts = useMemo(() => exam?.parts || [], [exam]);
  const detailedGroups = useMemo(() => {
    if (!result) return [];
    const respMap = {};
    (result.results || []).forEach((part) => {
      (part.questions || []).forEach((q) => {
        respMap[q.questionId] = q;
      });
    });
    return examParts.flatMap((part) =>
      (part.questionGroup || []).map((group) => ({
        type: "regular",
        partNumber: part.partNumber,
        audioUrl: group.audioUrl || null,
        imageUrl: group.imageUrl || null,
        extraImageUrl: group.extraImageUrl || null,
        content: group.content || null,
        questions: (group.questions || []).map((q) => {
          const r = respMap[q._id];
          const correctAnswer = r?.correctAnswer;
          return {
            question: {
              ...q,
              choices: (q.choices || []).map((c) => ({
                ...c,
                isCorrect:
                  correctAnswer != null
                    ? c.value === correctAnswer
                    : c.isCorrect,
              })),
            },
            userAnswer: r?.selectedOption ?? null,
            isCorrect: !!r?.isCorrect,
            answered: r?.selectedOption != null && r?.selectedOption !== "",
          };
        }),
      }))
    );
  }, [examParts, result]);

  const handleRetake = useCallback(() => {
    submittedRef.current = false;
    setResult(null);
    setAnswers({});
    setMarkedQuestions({});
    startedAtRef.current = new Date().toISOString();
    resetFlow();
  }, [resetFlow]);

  // ── Render ──────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (isError || !exam) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error?.data?.message || "Could not load this mock test."}
        </Alert>
        <Button variant="outlined" onClick={() => navigate("/mock-tests")}>
          Back to tests
        </Button>
      </Container>
    );
  }
  if (result) {
    return (
      <Result
        result={result}
        detailedGroups={detailedGroups}
        examTitle={exam.title}
        isAuthenticated={isAuthenticated}
        onRetake={handleRetake}
      />
    );
  }
  if (showWelcomeScreen) {
    return (
      <WelcomeScreen
        exam={exam}
        setShowInstructions={setShowInstructions}
        collapseSidebar={collapseSidebar}
        setHasUserInteracted={setHasUserInteracted}
        setShowWelcomeScreen={setShowWelcomeScreen}
        setAudioVolume={setAudioVolume}
        disableExport
        onStart={async () => {
          if (!startedAtRef.current) {
            startedAtRef.current = new Date().toISOString();
          }
          return true;
        }}
      />
    );
  }
  if (!currentPart) {
    return (
      <Container maxWidth="xl" sx={{ py: 5 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="50vh"
        >
          <Typography variant="h6" color="text.secondary">
            Loading exam data...
          </Typography>
        </Box>
      </Container>
    );
  }

  // Submit is reachable at the last question in part mode (any section) or
  // at the last Reading question in full mode — same as ExamSession.
  const showSubmit =
    isLastQuestion &&
    (mode === "part" || currentSection === SECTION.READING);

  const showNavBar =
    (isFinishListening && currentSection === SECTION.LISTENING) ||
    (currentSection === SECTION.READING && !showInstructions) ||
    (mode === "part" && !showInstructions);

  return (
    <>
      <ConfirmSubmitDialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
        onConfirm={() => {
          setOpenConfirmDialog(false);
          doSubmit();
        }}
        message={
          mode === "part"
            ? "Submit your part practice and see your results?"
            : "Are you sure you want to submit the TOEIC test?"
        }
        isSubmitting={isSubmitting}
      />
      <ExamAudioElement
        ref={audioRef}
        onPlay={handleAudioPlay}
        onEnded={handleAudioEnded}
        currentSection={currentSection}
        isPlaying={isPlaying}
      />
      {showInstructions ? (
        <PartInstruction currentPart={currentPart} />
      ) : (
        <ExamQuestionView
          examTitle={exam.title}
          currentPart={currentPart}
          currentGroup={currentGroup}
          currentSection={currentSection}
          answers={answers}
          onAnswerSelect={handleAnswerSelect}
          markedQuestions={markedQuestions}
          onToggleMark={handleToggleMarkQuestion}
          showTimer={mode === "full" && currentSection === SECTION.READING}
          timeLeft={timeLeft}
          showSubmit={showSubmit}
          isSubmitting={isSubmitting}
          onSubmit={() => setOpenConfirmDialog(true)}
          topSlot={
            mode === "part" ? (
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                Practice mode — no countdown. Scoring is per selected part:{" "}
                {parts.map((p) => partLabel(p)).join(", ")}.
              </Alert>
            ) : null
          }
        />
      )}
      {showNavBar && (
        <NavigationBar
          handleNext={handleNext}
          handlePrev={handlePrev}
          currentGroup={currentGroup}
          questionGroup={questionGroup}
          currentPartIndex={currentPartIndex}
          parts={examParts}
          currentGroupIndex={currentGroupIndex}
          isWaiting={isWaiting}
          showInstructions={showInstructions}
          handleOpenQuestionList={() => setOpenQuestionList(true)}
        />
      )}
      <QuestionsDialog
        openQuestionList={openQuestionList}
        handleCloseQuestionList={() => setOpenQuestionList(false)}
        getAllReadingQuestions={allQuestions}
        navigateToQuestion={handleNavigateToQuestion}
        answers={answers}
        markedQuestions={markedQuestions}
      />
    </>
  );
};

export default Runner;
